// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

// --- Services ---
import parameterSvc from "../../services/api/parameter";

// --- Models ---
import { Parameter, ParameterFilter } from "../../models/types/parameter";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";
import MenuItem from "@mui/material/MenuItem";
import ButtonMore from "../../components/button/buttonMore";

// --- Utils ---
import dayjs from "dayjs";
import { useResponsive } from "../../hooks/mediaQuery";

// --- Icons ---
import { Trash2, Pencil, ListMinus } from "lucide-react";
import ParameterFilterView from "./filter";
import { useTranslation } from "react-i18next";

export default function ParameterPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { t } = useTranslation();
    const { isXs, isLg, isXl } = useResponsive();

    const [data, setData] = useState<Parameter[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<Parameter | null>(null);

    const getListAll = async (params: DefaultFilterRequest<ParameterFilter>) => {
        setLoading(true);
        try {
            const res = await parameterSvc.getAll(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify(t('common.error'), t('common.errorMessage'), "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: Parameter) => {
        try {
            const res = await parameterSvc.deleteParameter(row.id);
            if (res && res.content.status) {
                notify(t('common.success'), t('parameter.notifications.deleteSuccess'), "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify(t('common.error'), t('common.errorMessage'), "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const params: DefaultFilterRequest<ParameterFilter> = {
            sorting: queryParams.get("sorting"),
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            name: queryParams.get("name"),
            unitId: queryParams.get("unitId"),
            isDelete: queryParams.get("isDelete") || "false",
        };

        await getListAll(params);
    };
    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleEdit = (row: Parameter) => {
        navigate(UrlCollection.EditParameter.replace(":id", row.id.toString()));
    };

    const handleDelete = (row: Parameter) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const columns = [
        { header: t('parameter.table.stt'), accessor: "index", sortable: false, className: "w-[50px]" },
        { header: t('parameter.table.name'), accessor: "name", sortable: true },
        { header: t('parameter.table.description'), accessor: "description", sortable: true, className: "min-w-[100px]" },
        { header: t('parameter.table.unit'), accessor: "unitName", sortable: true },
        {
            header: t('parameter.table.modifiedDate'),
            accessor: (row: Parameter) => `${dayjs(row.modified_date).format("DD/MM/YYYY")}`,
            sortable: true,
        },
        {
            header: "",
            accessor: (row: Parameter) => {
                if (isLg || isXl) {
                    return (
                        <div className="flex gap-2">
                            <ButtonAction
                                onClick={() => handleEdit(row)}
                                icon={<Pencil size={14} />}
                                color="blue"
                                title={t('parameter.actions.edit')}
                            />
                            <ButtonAction
                                onClick={() => handleDelete(row)}
                                icon={<Trash2 size={14} />}
                                color="red"
                                title={t('parameter.actions.delete')}
                            />
                        </div>
                    )
                }
                return (
                    <ButtonMore
                        icon={<ListMinus size={14} />}
                        title="Edit"
                    >
                        <MenuItem onClick={() => handleEdit(row)}>
                            <Pencil size={14} color="blue" className="mr-2" />
                            <span>{t('parameter.actions.edit')}</span>
                        </MenuItem>
                        <MenuItem onClick={() => handleDelete(row)}>
                            <Trash2 size={14} color="red" className="mr-2" />
                            <span>{t('parameter.actions.delete')}</span>
                        </MenuItem>
                    </ButtonMore>
                )
            },
            className: "max-w-[100px]",
        },
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header bg-gray-100">
                <h1 className="text-xl font-semibold">{t('parameter.title')}</h1>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateParameter)}
                >
                    {t('parameter.addNew')}
                </button>
            </div>
            <div className={`flex ${isXs ? 'my-1' : 'my-2'}`}>
                <ParameterFilterView />
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title={t('common.confirm')}
                    content={`${t('parameter.modal.confirmDelete.content')} ${selectedRow?.name}${t('parameter.modal.confirmDelete.contentNo')}`}
                    titleComfirm={t('common.confirm')}
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
        </div>
    );
}
